import {FunnelLayout, FunnelProvider, FunnelRoutes} from '@tinct/web-funnel';
import {useMemo, useState} from 'react';
import {Step1FillWebsiteUrl} from './steps/step1-fill-website-url';
import {Step2Analyzing} from './steps/step2-analyzing/step2-analyzing';
import {Step3CompanyProfile} from './steps/step3-company-profile';
import {useCurrentWorkspaceUrlPrefix, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {Step4ValueMessaging} from "./steps/step4-value-messaging";
import {Step5VisualIdentity} from "./steps/step5-visual-identity";
import {Step6Compliance} from "./steps/step6-compliance";

import {ArrowRightIcon} from "lucide-react";
import {Button, FunnelDropdownOptions} from "@tinct/web-components";
import {useGetOrganizationById} from "@tinct/core-api";
import {QueryObserverResult, RefetchOptions} from "@tanstack/react-query";
import {Organization} from "@tinct/core-api-models";
import {useNavigate} from "react-router-dom";
import {isAwaitedExtractionCompleted} from "./followed-extraction";

export type BrandExtractionFunnelState = {
  websiteUrl: string | null;
  refetchOrganization: (options?: RefetchOptions) => Promise<QueryObserverResult<Organization | null>>;
  /**
   * Says a new extraction is wanted (a website was just given): from now on the funnel waits for an
   * extraction newer than the latest one, instead of taking a finished previous job for this one.
   */
  requestExtraction: () => void;
  /**
   * The job that was the latest when the extraction was asked for in this visit, recorded once by the
   * funnel and handed to the Processing step, so both wait for the same extraction; `undefined` when
   * none was asked for (the step then decides on its own first load, as after a reload).
   */
  previousExtractionJobId?: string | null;
};


export const BrandExtractionFunnel = () => {
  const currentWorkspaceId = useGetCurrentWorkspaceId();
  const { data: organization, refetch: refetchOrganization } = useGetOrganizationById({
    id: currentWorkspaceId,
    fields: {
      // The latest extraction, not the job that produced the latest version:
      // during a re-extraction the latter is the previous, completed one.
      brandExtractionJob: {
        fields: {
          id: null,
          status: null,
        },
      },
      domain: null,
    }
  })

  const [funnelState, setFunnelState] = useState<BrandExtractionFunnelState>({
    websiteUrl: null,
    refetchOrganization,
    requestExtraction: () => setExtractionRequest('asked'),
  });

  // A new extraction asked for in this visit: `asked` at once, then the job that was the latest at
  // that moment, which the funnel never takes for the awaited one. None asked for, any completed
  // latest job means the brand is extracted. The Processing step's own rule (followed-extraction).
  const [extractionRequest, setExtractionRequest] = useState<
    null | 'asked' | { previousJobId: string | null }
  >(null);
  if (extractionRequest === 'asked') {
    const previousJobId = organization?.brandExtractionJob?.id ?? null;
    setExtractionRequest({ previousJobId });
    // The Processing step waits for the same extraction: it gets this id rather than working it out
    // from its own query, which a delay between the two could make disagree
    setFunnelState((state) => ({ ...state, previousExtractionJobId: previousJobId }));
  }

  const extractionJobDone =
    extractionRequest !== 'asked' &&
    isAwaitedExtractionCompleted(organization?.brandExtractionJob, extractionRequest?.previousJobId ?? null);

  const navigate = useNavigate();


  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();

  // The step forms, as callback refs kept in state: a form element only exists
  // once its step has loaded the brand into it, so "Next step" stays disabled
  // until then instead of submitting the empty defaults.
  const [step3Form, setStep3Form] = useState<HTMLFormElement | null>(null);
  const [step4Form, setStep4Form] = useState<HTMLFormElement | null>(null);
  const [step5Form, setStep5Form] = useState<HTMLFormElement | null>(null);
  const [step6Form, setStep6Form] = useState<HTMLFormElement | null>(null);

  const finishUrl = `${currentWorkspaceUrlPrefix}/dashboard`;

  // Wrapper components that capture the refs. Memoized: a new component type
  // per render would remount the step (and its form) on every render of the
  // funnel, which the ref state above now triggers.
  const { Step3WithRef, Step4WithRef, Step5WithRef, Step6WithRef } = useMemo(() => ({
    Step3WithRef: () => <Step3CompanyProfile ref={setStep3Form} />,
    Step4WithRef: () => <Step4ValueMessaging ref={setStep4Form} />,
    Step5WithRef: () => <Step5VisualIdentity ref={setStep5Form} />,
    Step6WithRef: () => <Step6Compliance ref={setStep6Form} onSuccess={() => navigate(finishUrl)} />,
  }), [navigate, finishUrl]);

  const FunnelOption = <FunnelDropdownOptions options={[
    {
      label: 'Leave Funnel',
      to: finishUrl}
  ]} />

  if(!organization) {
    return null;
  }

  return (
    <FunnelProvider
      steps={[
        {
          label: 'Fill website',
          id: 'fill-website',
          path: 'fill-website',
          availabilityFn: () => organization?.domain == null,
          showInStepper: false,
          showStepper: false,
          component: Step1FillWebsiteUrl,
        },
        {
          label: 'Processing',
          id: 'processing',
          path: 'processing',
          availabilityFn: () => !extractionJobDone,
          showInStepper: false,
          showStepper: false,
          showBackButton: false,
          actions: [],
          component: Step2Analyzing,
        },
        {
          optionComponent: FunnelOption,
          label: 'Company Profile',
          id: 'company-profile',
          path: 'company-profile',
          availabilityFn: () => true,
          showInStepper: true,
          showStepper: true,
          actions: [{
            id: 'save',
            label: (
              <Button disabled={!step3Form} onClick={() => step3Form?.requestSubmit()}>
                Next step <ArrowRightIcon />
              </Button>
            ),
          }],
          component: Step3WithRef,
        },
        {
          optionComponent: FunnelOption,
          label: 'Value & Messaging',
          id: 'value-messaging',
          path: 'value-messaging',
          availabilityFn: () => true,
          showInStepper: true,
          showStepper: true,
          actions: [{
            id: 'save',
            label: (
              <Button disabled={!step4Form} onClick={() => step4Form?.requestSubmit()}>
                Next step <ArrowRightIcon />
              </Button>
            ),
          }],
          component: Step4WithRef,
        },
        {
          optionComponent: FunnelOption,
          label: 'Visual Identity',
          id: 'visual-identity',
          path: 'visual-identity',
          availabilityFn: () => true,
          showInStepper: true,
          showStepper: true,
          actions: [{
            id: 'save',
            label: (
              <Button disabled={!step5Form} onClick={() => step5Form?.requestSubmit()}>
                Next step <ArrowRightIcon />
              </Button>
            ),
          }],
          component: Step5WithRef,
        },
        {
          optionComponent: FunnelOption,
          label: 'Compliance',
          id: 'compliance',
          path: 'compliance',
          availabilityFn: () => true,
          showInStepper: true,
          showStepper: true,
          actions: [{
            id: 'save',
            label: (
              <Button disabled={!step6Form} onClick={() => step6Form?.requestSubmit()}>
                Finish <ArrowRightIcon />
              </Button>
            ),
          }],
          component: Step6WithRef,
        },
      ]}
      state={funnelState}
      setState={setFunnelState}
      baseUrl={`${currentWorkspaceUrlPrefix}/brand`}
    >
      <FunnelLayout contentClassName="flex-1 flex">
        <FunnelRoutes />
      </FunnelLayout>
    </FunnelProvider>
  );
};
